import { useCallback, useEffect, useState } from 'react';
import { api } from '../onboarding/api.ts';
import { useSession, type WorkspaceSession } from '../../state/session.ts';
import { useOnboarding } from '../onboarding/store.ts';
import {
  message,
  readOwner,
  saveOwner,
  verifyLetter,
  type AuthorizationConfig,
  type AuthorizationInput,
  type DomainLetterResult,
  type Letter,
} from './model.ts';

export function useAuthorizations(pendingWorkspace?: WorkspaceSession | null, watch = false) {
  const currentWorkspace = useSession((s) => s.workspace);
  const actor = useSession((s) => s.actor);
  const workspace = pendingWorkspace ?? currentWorkspace;
  const savedSession = useOnboarding((s) => s.server.sessionId);
  const session = workspace?.setupSessionId ?? savedSession ?? '';
  // Load by id: onboarding rebuilds the workspace object on every store change, and reloading
  // then would clear an error the owner has not read yet.
  const workspaceId = workspace?.id;
  const authorizationId = workspace?.authorizationId;
  const [opening, setOpening] = useState(false);
  const [config, setConfig] = useState<AuthorizationConfig | null>(null);
  const [letters, setLetters] = useState<Letter[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const load = useCallback(async () => {
    if (!workspaceId) return;
    try {
      const [c, list] = await Promise.all([
        api.authorizationConfig(),
        !session && !actor && authorizationId
          ? api
              .ownerAuthorization(authorizationId, readOwner(authorizationId))
              .then((l) => ({ letters: [l] }))
          : api.authorizations(workspaceId, session),
      ]);
      setConfig(c);
      setLetters(list.letters);
      setError('');
    } catch (e) {
      setError(message(e));
    } finally {
      setLoading(false);
    }
  }, [workspaceId, authorizationId, session, actor]);
  // Resource loading updates state only after the network request settles.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load();
  }, [load]);
  const awaitingApproval = !letters[0]?.confirmedAt;
  useEffect(() => {
    if (!watch || !awaitingApproval) return;
    const check = () => {
      if (document.visibilityState === 'visible') void load();
    };
    const timer = window.setInterval(check, 5000);
    window.addEventListener('focus', check);
    document.addEventListener('visibilitychange', check);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', check);
      document.removeEventListener('visibilitychange', check);
    };
  }, [watch, awaitingApproval, load]);
  const refresh = async () => {
    setLoading(true);
    setError('');
    await load();
  };
  async function create(input: AuthorizationInput) {
    if (!workspace) return false;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const letter = await api.createAuthorization(workspace.id, session, input);
      setLetters((ls) => [letter, ...ls]);
      setNotice(
        `Review email accepted for delivery to ${input.email}. Open it to approve the exact terms within 30 minutes.`,
      );
      return letter;
    } catch (e) {
      setError(message(e));
      await refresh();
      setError(message(e));
      return false;
    } finally {
      setBusy(false);
    }
  }
  async function resend(id: string) {
    setBusy(true);
    setError('');
    try {
      await api.resendAuthorization(id, session);
      setNotice('A new review email was accepted for delivery.');
      await load();
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  }
  async function enterWorkspace(id: string) {
    setOpening(true);
    setError('');
    try {
      return await api.authorizationWorkspace(id, session, readOwner(id));
    } catch (e) {
      setError(message(e));
      return null;
    } finally {
      setOpening(false);
    }
  }
  return {
    workspace,
    config,
    letters,
    loading,
    busy,
    error,
    notice,
    refresh,
    create,
    resend,
    enterWorkspace,
    opening,
  };
}

export function useOnboardingEmail(workspace: WorkspaceSession) {
  const state = useAuthorizations(workspace, true);
  const [review, setReview] = useState<
    (Letter & { challengeId: string; linkExpiresAt: string }) | null
  >(null);
  const [verifiedCode, setVerifiedCode] = useState('');
  const [confirmed, setConfirmed] = useState<Letter | null>(null);
  const [codeBusy, setCodeBusy] = useState(false);
  const [codeError, setCodeError] = useState('');
  async function verifyCode(code: string) {
    const letter = state.letters[0];
    if (!letter) return false;
    setCodeError('');
    if (!/^\d{8}$/.test(code.replace(/\s/g, ''))) {
      setCodeError('Enter the eight-digit code from your latest email.');
      return false;
    }
    setCodeBusy(true);
    try {
      const result = await api.previewAuthorizationCode(
        letter.id,
        workspace.setupSessionId ?? '',
        code,
      );
      setVerifiedCode(code);
      setReview(result);
      return true;
    } catch (e) {
      setCodeError(message(e));
      return false;
    } finally {
      setCodeBusy(false);
    }
  }
  async function approve() {
    if (!review) return;
    setCodeBusy(true);
    setCodeError('');
    try {
      const result = await api.confirmAuthorizationCode(
        review.id,
        workspace.setupSessionId ?? '',
        verifiedCode,
        review.challengeId,
        review.termsHash,
      );
      saveOwner(result.id, result.ownerToken);
      setConfirmed(result);
      setReview(null);
      setVerifiedCode('');
      await state.refresh();
    } catch (e) {
      setCodeError(message(e));
    } finally {
      setCodeBusy(false);
    }
  }
  return {
    ...state,
    letter: confirmed ?? state.letters[0],
    review,
    codeError,
    codeBusy,
    verifyCode,
    approve,
    resetReview: () => {
      setReview(null);
      setVerifiedCode('');
      setCodeError('');
    },
  };
}

export function useConfirmation() {
  const [token] = useState(
    () => new URLSearchParams(window.location.hash.slice(1)).get('token') ?? '',
  );
  const [letter, setLetter] = useState<
    (Letter & { purpose: 'approve' | 'manage'; linkExpiresAt: string }) | null
  >(null);
  const [confirmed, setConfirmed] = useState<Letter | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [workspace, setWorkspace] = useState<WorkspaceSession | null>(null);
  async function prepareWorkspace(id: string) {
    try {
      setWorkspace(await api.authorizationWorkspace(id, '', readOwner(id)));
    } catch (e) {
      setError(message(e));
    }
  }
  useEffect(() => {
    history.replaceState(null, '', window.location.pathname);
    let live = true;
    void api
      .previewAuthorization(token)
      .then((l) => {
        if (!live) return;
        setLetter(l);
        const extra = l as Letter & { alreadyConfirmed?: boolean; ownerToken?: string };
        if (extra.alreadyConfirmed && extra.ownerToken) {
          saveOwner(l.id, extra.ownerToken);
          setConfirmed(l);
          void prepareWorkspace(l.id);
        }
      })
      .catch((e: unknown) => {
        if (live) setError(message(e));
      });
    return () => {
      live = false;
    };
  }, [token]);
  async function confirm() {
    if (!letter) return;
    setBusy(true);
    setError('');
    try {
      const result = await api.confirmAuthorization(token, letter.termsHash);
      saveOwner(result.id, result.ownerToken);
      setConfirmed(result);
      await prepareWorkspace(result.id);
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  }
  return { letter, confirmed, workspace, busy, error, confirm, prepareWorkspace };
}

// Public verification starts from the customer's domain alone: the backend reads the signed
// letter from their DNS, and this page re-checks its signature against Traceira's public keys.
export function useVerification(domain: string) {
  const [result, setResult] = useState<DomainLetterResult | null>(null);
  const [signature, setSignature] = useState<'checking' | 'verified' | 'unavailable'>('checking');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const load = useCallback(async () => {
    if (!domain) return;
    try {
      const [r, k] = await Promise.all([api.domainLetter(domain), api.publicKeys()]);
      const valid = r.letter ? await verifyLetter(r.letter, k.keys) : false;
      setResult(r);
      setSignature(valid ? 'verified' : 'unavailable');
      setError('');
    } catch (e) {
      setError(message(e));
      setSignature('unavailable');
    } finally {
      setBusy(false);
    }
  }, [domain]);
  // Resource loading updates state only after the network request settles.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load();
  }, [load]);
  const refresh = async () => {
    setBusy(true);
    setError('');
    setSignature('checking');
    await load();
  };
  return { result, signature, busy, error, refresh };
}

// Owner management stays in the app: the recorded authorizer's token opens it, never the
// public verification page.
export function useOwner(id: string) {
  const [letter, setLetter] = useState<(Letter & { privateJws: string }) | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const load = useCallback(async () => {
    const token = readOwner(id);
    if (!token) return;
    try {
      setLetter(await api.ownerAuthorization(id, token));
    } catch (e) {
      setError(message(e));
    }
  }, [id]);
  // Resource loading updates state only after the network request settles.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load();
  }, [load]);
  async function managementEmail() {
    setBusy(true);
    setError('');
    try {
      await api.managementEmail(id);
      setNotice('A management email was sent to the recorded authorizer.');
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  }
  async function revoke() {
    setBusy(true);
    setError('');
    try {
      await api.revokeAuthorization(id, readOwner(id));
      setNotice(
        'Authorization and the exclusive provider policy are revoked. Remove the TXT record from your DNS too.',
      );
      await load();
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  }
  async function enterWorkspace() {
    setBusy(true);
    setError('');
    try {
      const session = useOnboarding.getState().server.sessionId ?? '';
      const ws = await api.authorizationWorkspace(id, session, readOwner(id));
      useSession.getState().start(ws);
      return ws;
    } catch (e) {
      setError(message(e));
      return null;
    } finally {
      setBusy(false);
    }
  }
  return {
    letter,
    isOwner: Boolean(readOwner(id)),
    busy,
    error,
    notice,
    managementEmail,
    revoke,
    enterWorkspace,
  };
}
